<template>
    <div class="row mb-4">
        <options-question name="schemeOption"
                          question=""
                          :options="availablePensionOptionNames"
                          v-model:selected="localSelectedOptionName" />
    </div>
    <div class="row mb-4">
        <DBOptionsChart :graph-data="incomeGraphData"></DBOptionsChart>
    </div>

    <DBOptionsStats
        :life-expectancy="(getAgeAtEndOfPlanningPeriod() ?? 0).toString()"
        :breakevenAge="breakevenAge"
        :crossoverAge="crossoverAge"
        :averageSchemeIncreases="averageSchemeIncreases"
        :showPIEStats="showPIEStats"
        :averagePIEIncreases="averagePIEIncreases"
        :selectedOption="selectedOption"
        :user-has-multiple-options="userHasMultipleOptions" />

    <div class="row mt-3">
        <p>You can use the below sliders to explore how they impact the chart and statistics above.</p>
    </div>

    <div class="row mt-1 text-center">
        <div class="col-12 col-md-6 mb-0 d-flex align-items-stretch mb-5 mb-md-0">
            <div class="info-box pt-1 px-1 rounded">
                <div class="d-flex flex-column align-items-center justify-content-center pt-3 bg-white rounded-top">
                    <TaxFreeLumpSumDescriber :model="taxFreeCashModelSupporter" :selected-option="selectedOption" />
                </div>
                <div class="d-flex align-items-center w-100 px-3 mt-0 pb-3 mb-3 bg-white rounded-bottom">
                    <span class="me-md-2 mb-2 mb-md-0">0</span>
                    <input type="range"
                           class="form-range w-100 mx-md-2 mb-2 mb-md-0"
                           v-model.number="taxFreeCashModel"
                           min="0"
                           :max="taxFreeCashModelSupporter.sliderMax"
                           :step="taxFreeCashModelSupporter.step" />
                    <span class="ms-md-2 mb-2 mb-md-0">Max</span>
                    <div class="m-1">
                        <help title="Tax free cash maximum" :text="taxFreeCashModelSupporter.getOptionsDescription(finalSalaryOptionsStatic)" />
                    </div>
                </div>

                <div class="p-2">
                    <h4 class="fw-bold">Tax free cash wanted</h4>
                    <p v-for="tfcTextElement, i of tfcText" :key="i">{{ tfcTextElement }}</p>
                </div>
            </div>
        </div>

        <div class="col-12 col-md-6 mb-0 d-flex align-items-stretch">
            <div class="info-box pt-1 px-1 rounded">
                <div class="d-flex align-items-center justify-content-center pt-3 bg-white rounded-top">
                    <span>
                        <p class="mb-0">
                            Future inflation: <span class="d-inline-block text-end font-monospace text-nowrap fs-5" style="min-width: 5ch;">{{ futureInflationModel }}%</span>
                        </p>
                    </span>
                </div>
                <div class="d-flex align-items-center w-100 px-3 mt-0 pb-3 mb-3 bg-white rounded-bottom">
                    <span class="me-md-2 mb-2 mb-md-0">0</span>
                    <input type="range"
                           class="form-range w-100 mx-md-2 mb-2 mb-md-0"
                           v-model="futureInflationModel"
                           min="0"
                           max="5"
                           step="0.1" />
                    <span class="ms-md-2 mb-2 mb-md-0">5%</span>
                    <div class="m-1">
                        <help title="Future inflation" :text="htmlArray('dboptions.important-info.inflation.read-more.body')" />
                    </div>
                </div>

                <div class="p-2">
                    <h4 class="fw-bold">Future expected inflation</h4>
                    <p>Use the slider to explore how different levels of inflation could impact your retirement income.</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, Ref } from "vue";
    import Help from "@/guiide/components/common/Help.vue";
    import TaxFreeLumpSumDescriber from "@/guiide/components/dboptions/TaxFreeLumpSumDescriber.vue";
    import OptionsQuestion from "@/guiide/components/common/OptionsQuestion.vue";
    import { formatter } from "@/shared/ts/formatter";
    import TaxFreeCashModelSupporter from "@/guiide/ts/assets/finalsalary/taxFreeCashModelSupporter";
    import DBOptionsChart from "@/guiide/components/charts/DBOptionsChart.vue";
    import { ChartData, X, Y } from "@/guiide/ts/charts/chartDataPoint";
    import { FinalSalaryOptionInfoStatic } from "@/guiide/ts/assets/finalsalary/finalSalaryOptionInfoStatic";
    import FinalSalaryOption from "@/guiide/ts/assets/finalsalary/finalSalaryOption";
    import FinalSalarySchemeOptionsDetailResponse from "@/guiide/ts/response/finalSalarySchemeOptionsDetailResponse";
    import DBOptionsStats from "@/guiide/components/dboptions/DBOptionsStats.vue";
    import FinalSalarySchemeOptionsDetail from "@/guiide/ts/response/finalSalarySchemeOptionsDetail";
    import { getAgeAtEndOfPlanningPeriod } from "@/guiide/ts/utilities";
    import { useFinalSalaryStaticOptions } from "@/guiide/ts/assets/finalsalary/useFinalSalaryOptionInfoStatic";
    import { useUIContent } from "@/guiide/ts/configuration/useUIContent";

    const props = defineProps({
        selectedOptionName: { type: String, required: true},
        availableOptionsStatic: { type: Object as () => FinalSalaryOptionInfoStatic[], required: true },
        taxFreeCashModelSupporter: { type: TaxFreeCashModelSupporter, required: true },
        fsOptionsDynamic: { type: Object as () => FinalSalarySchemeOptionsDetailResponse, required: true },
        avcPotAmount: { type: Number, required: true }
    });

    const selectedOptionStartingPension = defineModel<number>('selectedOptionStartingPension', { default: () => 0 });

    const emit = defineEmits([ "update:selectedOptionName" ]);

    const { htmlArray, templateTextArray } = useUIContent();

    const ageChartDataIndex = X, amountChartDataIndex = Y;
    const averageSchemeIncreases = ref(2.5);
    const averagePIEIncreases = ref(2.5);
    const crossoverAge = ref("");
    const breakevenAge = ref("");
    const futureInflationModel = ref(2.5);

    const { finalSalaryOptionsStatic } = useFinalSalaryStaticOptions();
    const standardOption = ref<FinalSalarySchemeOptionsDetail>(props.fsOptionsDynamic.options.find(fsoi => fsoi.type == FinalSalaryOption.SchemeDefault));
    const pieOption = ref<FinalSalarySchemeOptionsDetail>(props.fsOptionsDynamic.options.find(fsoi => fsoi.type == FinalSalaryOption.PensionIncreaseExchange));
    const bpoOption = ref<FinalSalarySchemeOptionsDetail>(props.fsOptionsDynamic.options.find(fsoi => fsoi.type == FinalSalaryOption.BridgingPension));

    const chartDataPointFirstYearIndex = 0;
    const chartDataPointAmountIndex = 1;

    const showPIEStats = computed<boolean>(() => {
        const pieOptionDetail = props.fsOptionsDynamic.options.find(o => o.type == FinalSalaryOption.PensionIncreaseExchange);
        if (pieOptionDetail?.show ?? false) {
            return true;
        }

        const pieBPOptionDetail = props.fsOptionsDynamic.options.find(o => o.type == FinalSalaryOption.PIEBP);
        if (pieBPOptionDetail?.show ?? false) {
            return true;
        }

        return false;
    });

    const userHasMultipleOptions = computed<boolean>(() => props.availableOptionsStatic.length > 1);

    const localSelectedOptionName = computed({
        get: () => props.selectedOptionName,
        set: (value) => emit('update:selectedOptionName', value)
    });

    const selectedOption = computed<FinalSalaryOption>(() => finalSalaryOptionsStatic.value.optionFromString(localSelectedOptionName.value));

    const showPIEDetails = computed<boolean>(() => selectedOption.value == FinalSalaryOption.PensionIncreaseExchange);
    const showBridgingPensionDetails = computed<boolean>(() => selectedOption.value == FinalSalaryOption.BridgingPension);

    const crossoverAgeRelevant = computed<boolean>(() => finalSalaryOptionsStatic.value.get(selectedOption.value)?.crossoverAgeRelevant);

    const incomeGraphData = computed(() => {
        const chartData: ChartData = [];

        if (!props.fsOptionsDynamic) {
            const emptySeries = createGraphSeries(chartData, "", "");
            return [ emptySeries ];
        }

        const schemeGraphData = getDataForGraph(FinalSalaryOption.SchemeDefault, standardOption.value?.maxTaxFreeCash ?? 0, averageSchemeIncreases, 'Standard pension payments', '#99acb0');
        let selectedOptionGraphData = schemeGraphData;

        let optionGraphData: any = null;
        if (showPIEDetails?.value) {
            optionGraphData = getDataForGraph(FinalSalaryOption.PensionIncreaseExchange, pieOption.value?.maxTaxFreeCash ?? 0, averagePIEIncreases, 'PIE option payments', '#00bfff');
            selectedOptionGraphData = optionGraphData;
        } else if (showBridgingPensionDetails?.value) {
            optionGraphData = getDataForGraph(FinalSalaryOption.BridgingPension, bpoOption.value?.maxTaxFreeCash ?? 0, undefined, 'BP option payments', '#cf9465');
            selectedOptionGraphData = optionGraphData;
        }

        selectedOptionStartingPension.value = selectedOptionGraphData.data[chartDataPointFirstYearIndex][chartDataPointAmountIndex];

        const crossover = optionGraphData
            ? getCrossoverAge(schemeGraphData.data, optionGraphData.data)
            : null;
        crossoverAge.value = crossover == null || !crossoverAgeRelevant.value ? "n/a" : Math.floor(crossover).toString();

        const comparativeOptionSelected = selectedOption.value != FinalSalaryOption.SchemeDefault;
        if (comparativeOptionSelected) {
            const breakeven = optionGraphData
                ? identifyBreakeven(crossover, schemeGraphData.data, optionGraphData.data)
                : null;
            breakevenAge.value = breakeven == null ? (getAgeAtEndOfPlanningPeriod() ?? 0).toString() + "+" : breakeven.toString();
        } else {
            breakevenAge.value = "n/a";
        }

        const data = [schemeGraphData];
        if (optionGraphData) {
            data.push(optionGraphData);
        }

        return data;
    });

    const taxFreeCashModel = computed({
        get: () => props.taxFreeCashModelSupporter.selected,
        set: (newValue: number) => {
            props.taxFreeCashModelSupporter.selected = newValue;
            props.taxFreeCashModelSupporter.refresh();
        }
    });

    const tfcText = computed<string[]>(() => {
        const avcPotAmountFormatted = formatter.formatCurrency(props.avcPotAmount);
        const variantKey = props.avcPotAmount == 0 ? "without-avc-pot" : "with-avc-pot";
        return templateTextArray("dboptions.modelling.tfc.body.text", variantKey, { avcPotAmount: avcPotAmountFormatted });
    });

    const availablePensionOptionNames = computed<string[]>(() => props.availableOptionsStatic.map(item => item.name));

    function getDataForGraph(option: FinalSalaryOption, maxTaxFreeCash: number, schemeIncreases: Ref<number> | undefined, seriesName: string, seriesColour: string) {
        const charts = props.fsOptionsDynamic.options[option].charts;
        const taxFreeCashTotal = Math.min(maxTaxFreeCash, props.taxFreeCashModelSupporter?.selected ?? 0);
        const taxFreeCashFromPension = Math.max(0, taxFreeCashTotal);
        const taxFreeCashInterval = props.fsOptionsDynamic.taxFreeCashSliderInterval;
        const dataRange = props.fsOptionsDynamic.yearsToProject + 1;

        const dataForChart = getChartData(charts, taxFreeCashFromPension, futureInflationModel.value, taxFreeCashInterval, dataRange);

        if(schemeIncreases) {
            schemeIncreases.value = dataForChart[0] / 100;
        }

        const chartData = dataForChart.slice(1);
        const chart = insertRetirementYears(chartData, props.fsOptionsDynamic.startingAge);
        return createGraphSeries(chart, seriesName, seriesColour);
    }

    function getChartData(data: number[], taxFreeCash: number, futureInflation: number, taxFreeCashInterval: number, dataRange: number): number[] {
        const taxFreeCashSteps = props.taxFreeCashModelSupporter.ticks;
        const taxFreeCashIndex = Math.ceil(taxFreeCash / taxFreeCashInterval);
        const inflationIndex = Math.round(futureInflation / 0.1);
        const chartIndex = inflationIndex * taxFreeCashSteps + taxFreeCashIndex;
        const startIndex = chartIndex * dataRange;
        return data.slice(startIndex, startIndex + dataRange);
    }

    function insertRetirementYears(chartData: number[], startYear: number = 0): number[][] {
        return chartData.map((amount, year) => [year + startYear, amount]);
    }

    function createGraphSeries(data: ChartData, name: string, color: string) {
        return {
            "name": name,
            "data": data,
            "visible": true,
            "color": color,
            "type": "line",
            "marker": {
                "enabled": false
            }
        }
    }

    function identifyBreakeven(crossoverAge: number | null, schemeIncome: ChartData, optionIncome: ChartData): number | null {
        const breakevenIsRelevant = showPIEDetails.value || showBridgingPensionDetails.value;

        if (crossoverAge == null || !breakevenIsRelevant) {
            return null;
        }

        let schemeTotal = 0;
        let optionTotal = 0;
        let runningDiff = 0;

        for (let retirementYear = 0; retirementYear < schemeIncome.length; retirementYear++) {
            const schemeIncomePoint = schemeIncome[retirementYear];
            const optionIncomePoint = optionIncome[retirementYear];

            schemeTotal += schemeIncomePoint[amountChartDataIndex];
            optionTotal += optionIncomePoint[amountChartDataIndex];
            runningDiff = optionTotal - schemeTotal;

            if (runningDiff < 0) {
                return schemeIncomePoint[ageChartDataIndex];
            }
        }

        return null;
    }

    function getCrossoverAge(schemeIncome: ChartData, optionIncome: ChartData): number | null {
        for (let i = 1; i < schemeIncome.length; i++) {
            const [agePrev, schemePrev] = schemeIncome[i - 1];
            const [, optionPrev] = optionIncome[i - 1];
            const [ageCurr, schemeCurr] = schemeIncome[i];
            const [, optionCurr] = optionIncome[i];

            const diffPrev = schemePrev - optionPrev;
            const diffCurr = schemeCurr - optionCurr;

            if (diffPrev === 0) {
                return agePrev;
            }

            if (diffPrev * diffCurr < 0) {
                const proportion = diffPrev / (diffPrev - diffCurr);
                const crossoverAge = agePrev + proportion * (ageCurr - agePrev);
                return crossoverAge;
            }
        }

        return null;
    }
</script>

<style scoped lang="css">
    .info-box {
        background-color: #f5f5f5;
        width: fit-content;
        min-height: 230px;
    }
</style>